import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MuiDialog from "@mui/material/Dialog";
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import useBoolean from "hooks/useBoolean";
import logEvent from "utils/logEvent";

const Dialog = styled(MuiDialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: "20px",
  },
}));

interface PageInfoCardAdornmentProps {
  maxWidth?: "md" | "lg";
  pageTitle: string;
  children: ({ onClose }: { onClose: () => void }) => JSX.Element;
}

/**
 * A reusable component that provides an info icon button which, when clicked,
 * opens a dialog containing the provided content. It is designed to be used as an
 * adornment in page headers or titles.
 *
 * Receives a children prop that is a function. This function is called with an object containing
 * an onClose method, which can be used to close the dialog from within the content.
 */
function PageInfoCardAdornment(props: PageInfoCardAdornmentProps) {
  const { maxWidth = "md", pageTitle } = props;
  const { t } = useTranslation();
  const { value: open, setValue } = useBoolean();

  const toggle = useCallback(() => {
    setValue((prev) => {
      const updatedValue = !prev;
      if (updatedValue) logEvent("INFO_CARD_VIEW", { page: pageTitle });
      return updatedValue;
    });
  }, [setValue, pageTitle]);

  return (
    <>
      <IconButton size="small" color="primary" onClick={toggle} aria-label={t("global.more_info_button_text")}>
        <InfoOutlined fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={toggle} maxWidth={maxWidth}>
        {props.children({ onClose: toggle })}
      </Dialog>
    </>
  );
}

export default PageInfoCardAdornment;
